import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

export default function NarratorField({ voices, value, onChange }) {
  const [sampling, setSampling] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);
  const sampleRef = useRef(null);
  const openaiCache = useRef({});

  const narratorVoices = useMemo(() => voices.filter((v) => v.voice_id.startsWith("narrator:")), [voices]);
  const openaiVoices = useMemo(() => voices.filter((v) => v.voice_id.startsWith("openai:")), [voices]);
  const otherVoices = useMemo(() => voices.filter((v) => !v.voice_id.startsWith("openai:") && !v.voice_id.startsWith("narrator:")), [voices]);

  const selectedVoice = voices.find((v) => v.voice_id === value);
  const previewUrl = selectedVoice?.preview_url;
  const isOpenAIVoice = !!value && value.startsWith("openai:");
  const isNarrator = !!value && value.startsWith("narrator:");
  const canSample = !!previewUrl || isOpenAIVoice || isNarrator;

  useEffect(() => () => sampleRef.current?.pause(), []);

  const toggleSample = async () => {
    if (sampling && sampleRef.current) {
      sampleRef.current.pause();
      sampleRef.current = null;
      setSampling(false);
      return;
    }
    let src = previewUrl;
    if (isOpenAIVoice || isNarrator) {
      src = openaiCache.current[value];
      if (!src) {
        setSampleLoading(true);
        try {
          const url = isNarrator ? `/voices/narrator-sample/${value.split(":")[1]}` : `/voices/openai-sample/${value.split(":")[1]}`;
          const { data } = await api.get(url);
          src = data.audio;
          openaiCache.current[value] = src;
        } catch (e) {
          toast.error(apiError(e));
          return;
        } finally {
          setSampleLoading(false);
        }
      }
    }
    if (!src) return;
    const audio = new Audio(src);
    audio.onended = () => setSampling(false);
    audio.onerror = () => setSampling(false);
    sampleRef.current = audio;
    audio.play().then(() => setSampling(true)).catch(() => setSampling(false));
  };

  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Narrator (AI voice)</label>
      <div className="flex gap-2">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid="create-voice-select"
          className="flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {voices.length === 0 && <option value="">Loading voices…</option>}
          {narratorVoices.length > 0 && (
            <optgroup label="AI Narrators — included (5 credits)">
              {narratorVoices.map((v) => (
                <option key={v.voice_id} value={v.voice_id}>
                  {v.name}{v.labels?.style ? ` · ${v.labels.style}` : ""}
                </option>
              ))}
            </optgroup>
          )}
          {openaiVoices.length > 0 && (
            <optgroup label="OpenAI — included (5 credits)">
              {openaiVoices.map((v) => (
                <option key={v.voice_id} value={v.voice_id}>
                  {v.name}{v.labels?.gender ? ` · ${v.labels.gender}` : ""}
                </option>
              ))}
            </optgroup>
          )}
          {otherVoices.length > 0 && (
            <optgroup label="ElevenLabs — 10 credits + uses your ElevenLabs quota">
              {otherVoices.map((v) => (
                <option key={v.voice_id} value={v.voice_id}>
                  {v.name}{v.labels?.gender ? ` · ${v.labels.gender}` : ""}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <button
          type="button"
          onClick={toggleSample}
          disabled={!canSample || sampleLoading}
          data-testid="create-voice-sample-btn"
          title={canSample ? "Play a sample of this voice" : "No sample available for this voice"}
          className="shrink-0 inline-flex items-center gap-1.5 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2.5 rounded-sm text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sampleLoading ? <Loader2 className="h-4 w-4 animate-spin text-blue-400" /> : sampling ? <Pause className="h-4 w-4 text-blue-400" /> : <Play className="h-4 w-4 text-blue-400" />}
          {sampleLoading ? "Loading" : sampling ? "Stop" : "Sample"}
        </button>
      </div>
      {isNarrator && selectedVoice && (
        <div className="mt-2 flex items-center gap-2.5 rounded-md border border-amber-500/20 bg-amber-500/5 px-2.5 py-2" data-testid="narrator-persona-info">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/narrators/${value.split(":")[1]}/avatar`}
            alt={selectedVoice.name}
            className="h-9 w-9 rounded-full shrink-0"
          />
          <p className="text-xs text-zinc-400">
            <span className="text-amber-300 font-semibold">{selectedVoice.name}</span> — {selectedVoice.description}. You can also show this narrator on screen from the video editor.
            {selectedVoice.intro && (
              <span className="block mt-1" data-testid="narrator-intro-line">
                Opens every episode with: <span className="italic text-zinc-300">“{selectedVoice.intro}”</span>
              </span>
            )}
          </p>
        </div>
      )}
      <p className="text-xs text-zinc-600 mt-1.5">Pick the voice that will narrate your video. You can change it later in the editor.</p>
      {!isOpenAIVoice && !isNarrator && value && (
        <p className="text-xs text-amber-400/90 mt-1.5" data-testid="elevenlabs-quota-warning">
          Heads up: ElevenLabs voices spend your ElevenLabs character quota (~1 credit per character — a long video ≈ 4,000 ElevenLabs credits). Included OpenAI voices use none.
        </p>
      )}
    </div>
  );
}
